import { BoxV2 as Box, Flex, Table, RoleGuard, useTable } from 'portal-commons';
import React, { useState, useEffect } from 'react';
import NumericLabel from 'react-pretty-numbers';
import { Link } from 'react-router-dom';
import { faCalendarDay } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Container, Grid } from '@material-ui/core';

import {
  getBrandStatistics,
  getCampaignDetail,
  getCampaignByPhone,
  getCampaignStatistics,
  getCampaignTopUseCase,
  getEventHistory,
  getPartnerCampaignStatistics,
} from '../apiServices';
import { EventTableRow, UseCaseTableRow, SearchTextField } from '../components';
import { MAX_EVENT_RECORDS } from '../../Events/constants';
import { S3_ASSETS_PATH } from '@/constants';
import {
  BRANDS_DEFAULT_ENTRY,
  CNP_CAMPAIGNS_DEFAULT_ENTRY,
  MY_CAMPAIGNS_DEFAULT_ENTRY,
} from '@/constants/paths';
import { withAuthContext } from '@/contexts';
import { createCampaignRedirectorPath } from '@/utils/path';
import { fieldValidation } from '@/utils/validator';

import partnerCampaignsIcon from '@/assets/images/partner-campaigns-icon.svg';

import '@/assets/styles/dashboard-new-module.scss';
import { GeneralAlertPopup } from '@/shared_elements';

const option = {
  justification: 'C',
  locales: 'en-AU',
  percentage: false,
  wholenumber: null,
  commafy: true,
  shortFormat: true,
  shortFormatMinValue: 10000,
  shortFormatPrecision: 2,
  title: true,
  cssClass: ['red'],
};

const EventTableHeaders = [
  {
    id: 'datetime',
    label: 'DATE & TIME',
    sortable: false,
  },
  {
    id: 'authenticatedUser',
    label: 'USER',
    sortable: false,
  },
  {
    id: 'description',
    label: 'DESCRIPTION',
    sortable: false,
  },
];

const DashboardNew = (props) => {
  const { history, location, profile } = props;

  const [brandInfo, setBrandInfo] = useState({});
  const [campaignInfo, setCampaignInfo] = useState({});
  const [partnerCampaignInfo, setPartnerCampaignInfo] = useState({});
  const [campaignSearchValue, setCampaignSearchValue] = useState('');
  const [campaignSearchError, setCampaignSearchError] = useState('');
  const [campaignSearching, setCampaignSearching] = useState(false);

  const eventTable = useTable(
    async (filter) => {
      const response = await getEventHistory({
        ...filter,
        recordsPerPage: 15,
      });
      return {
        data: response?.records || [],
        total: response?.total || 0,
        page: response?.page || 1,
        rowsPerPage: response?.recordsPerPage || 15,
      };
    },
    {
      deps: [],
      cappingTotal: MAX_EVENT_RECORDS,
      defaultFilter: {
        sortField: 'createDate',
        ascendingOrder: false,
      },
    }
  );

  const useCaseTable = useTable(
    async () => {
      const response = (await getCampaignTopUseCase()) || {};
      const data = Object.keys(response)
        .sort((a, b) => response[b] - response[a])
        .map((key) => ({ name: key, count: response[key] }));

      return {
        data: data || [],
        total: data?.length || 0,
        page: 1,
        rowsPerPage: data.length,
      };
    },
    { deps: [] }
  );

  const errorCode = {
    id: {
      0: '',
      2: 'Minimum 7 characters id',
      3: 'Maximum 7 characters id',
      4: 'Invalid id',
    },
    idObj: {
      regexPattern: /^[a-zA-Z0-9]{7}$/,
      maxLength: 7,
      minLength: 7,
    },
    phone: {
      0: '',
      2: 'Minimum 10 digits phone number',
      4: 'Invalid phone number',
    },
    phoneObj: {
      // refer to https://campaignreg.atlassian.net/wiki/x/CICuC
      regexPattern: /^\+?(?:[0-9][_!#$%&'*+\/=?`{|}~^.\-()\s]*?){6,14}[0-9]$/,
      minLength: 10,
    },
  };

  const isIdPattern = (value) => {
    return /^(?=.*[a-zA-Z])[a-zA-Z0-9]*$/.test(value);
  };

  const validator = (allowEmpty = true) => {
    if (!allowEmpty && !campaignSearchValue) {
      return 'This field is required';
    }
    let errorMsg = '';
    if (isIdPattern(campaignSearchValue)) {
      errorMsg =
        errorCode.id[
          fieldValidation({ ...errorCode.idObj, fieldval: campaignSearchValue })
        ];
    } else {
      errorMsg =
        errorCode.phone[
          fieldValidation({
            ...errorCode.phoneObj,
            fieldval: campaignSearchValue,
          })
        ];
    }
    return errorMsg;
  };

  const handleError = () => {
    const errorMsg = validator();
    setCampaignSearchError(errorMsg);
  };

  const handleSearchChange = (value) => {
    setCampaignSearchValue(value);
    setCampaignSearchError('');
  };

  const handleSearchClick = async () => {
    setCampaignSearching(true);
    const errorMsg = validator(false);
    if (!errorMsg) {
      const response = isIdPattern(campaignSearchValue)
        ? await getCampaignDetail(campaignSearchValue)
        : await getCampaignByPhone(campaignSearchValue);
      setCampaignSearching(false);
      if (response) {
        history.push({
          pathname: createCampaignRedirectorPath(response.uid),
          state: { goBackPage: location.pathname },
        });
      }
    } else {
      setCampaignSearchError(errorMsg);
      setCampaignSearching(false);
    }
  };

  useEffect(() => {
    document.title = 'The Campaign Registry - Dashboard';
    if (!(profile && profile.status.toLowerCase() === 'active')) {
      history.push('/');
      return;
    }

    getBrandStatistics().then((res) => {
      setBrandInfo(res);
    });

    getCampaignStatistics().then((res) => {
      setCampaignInfo(res);
    });

    getPartnerCampaignStatistics().then((res) => {
      setPartnerCampaignInfo(res);
    });
  }, [profile, history]);

  return (
    <section className="dashboard-section" data-testid="dashboard">
      <Container maxWidth={false} className="dashboard-container">
        <Grid container spacing={3} direction="row-reverse">
          <Grid item xs={5} justifyContent="flex-end">
            <SearchTextField
              label="Search by Campaign ID or Phone Number"
              value={campaignSearchValue}
              error={campaignSearchError}
              searching={campaignSearching}
              handleChange={handleSearchChange}
              handleError={handleError}
              handleSearchClick={handleSearchClick}
            />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid item xs={4}>
            <div className="statistics-wrapper">
              <div
                className="count-wrapper"
                data-testid="dashboardActiveCampaignsButton"
                onClick={() => history.push(MY_CAMPAIGNS_DEFAULT_ENTRY)}
              >
                <p className="paragraph">Active Campaigns</p>
                <div
                  className="count"
                  data-testid="dashboardActiveCampaignsCount"
                >
                  {['string', 'number'].includes(typeof campaignInfo.ACTIVE) ? (
                    <NumericLabel params={option}>
                      {campaignInfo.ACTIVE}
                    </NumericLabel>
                  ) : (
                    '--'
                  )}
                </div>
              </div>
              <RoleGuard feature="campaignList.addCampaign">
                <p data-testid="dashboardAddNewCampaignButton">
                  <Link to="/campaign/create" className="primary-btn">
                    +Add new Campaign
                  </Link>
                </p>
              </RoleGuard>
              <img
                src={`${S3_ASSETS_PATH}/images/dashboard-campaign.svg`}
                className="campaigns bg-img"
                alt="campaign"
              />
            </div>
          </Grid>
          <Grid item xs={4}>
            <div className="statistics-wrapper brand">
              <div
                data-testid="dashboardActiveBrandButton"
                className="count-wrapper"
                onClick={() => history.push(BRANDS_DEFAULT_ENTRY)}
              >
                <p className="paragraph">Active Brands</p>
                <div className="count" data-testid="dashboardActiveBrandCount">
                  {['string', 'number'].includes(typeof brandInfo.total) ? (
                    <NumericLabel params={option}>
                      {brandInfo.total}
                    </NumericLabel>
                  ) : (
                    '--'
                  )}
                </div>
              </div>
              <RoleGuard feature="brandList.addBrand">
                <p data-testid="dashboardActiveBrandButton">
                  <Link to="/brand/create" className="primary-btn">
                    +Add new Brand
                  </Link>
                </p>
              </RoleGuard>
              <img
                src={`${S3_ASSETS_PATH}/images/dashboard-brand.svg`}
                className="bg-img"
                alt="brand"
              />
            </div>
          </Grid>
          <Grid item xs={4}>
            <div className="statistics-wrapper partner-campaigns">
              <div
                data-testid="dashboardPendingPartnerCampaignsButton"
                className="count-wrapper"
                onClick={() => history.push(CNP_CAMPAIGNS_DEFAULT_ENTRY)}
              >
                <p className="paragraph">
                  Pending Connectivity Partner Campaigns
                </p>
                <div
                  className="count"
                  data-testid="dashboardPendingPartnerCampaignsCount"
                >
                  {['string', 'number'].includes(
                    typeof partnerCampaignInfo.total
                  ) ? (
                    <NumericLabel params={option}>
                      {partnerCampaignInfo.total}
                    </NumericLabel>
                  ) : (
                    '--'
                  )}
                </div>
              </div>
              <RoleGuard
                feature="cnpCampaignList.view"
                render={(authorized) => (
                  <p data-testid="dashboardPendingPartnerCampaignsButton">
                    <Link
                      to={CNP_CAMPAIGNS_DEFAULT_ENTRY}
                      className={
                        authorized ? 'primary-btn' : 'primary-btn disabled'
                      }
                    >
                      View
                    </Link>
                  </p>
                )}
              />
              <img
                src={partnerCampaignsIcon}
                className="bg-img"
                alt="partner campaigns"
              />
            </div>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={8}>
                <div className="events-listing wrapper">
                  <div className="titleRow">
                    <h3
                      className="heading1 text-center"
                      data-testid="dashboardEventsViewAllButton"
                    >
                      <Flex
                        sx={{
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box />
                        <Flex sx={{ alignItems: 'center' }}>
                          <Box sx={{ mr: 'xs' }}>
                            <FontAwesomeIcon icon={faCalendarDay} />
                          </Box>
                          <span>Events</span>
                        </Flex>
                        <Box>
                          <Link to="/events" className="primary-btn">
                            View all
                          </Link>
                        </Box>
                      </Flex>
                    </h3>
                  </div>
                  <Table
                    className="events-listing-table"
                    data-testid="eventListingTable"
                    disableHover
                    headRows={EventTableHeaders}
                    loading={eventTable.loading}
                    emptyState="no events to view"
                    tableData={eventTable?.tableData?.map((event) => (
                      <EventTableRow data={event} key={event.id} />
                    ))}
                  />
                </div>
              </Grid>
              <Grid item xs={4}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <div className="usecase-listing wrapper">
                      <div className="titleRow">
                        <h3 className="heading1 text-center">Use-cases</h3>
                      </div>
                      <Table
                        className="use-cases-table"
                        data-testid="dashboardTableCampaign"
                        disableHover
                        headRows={[
                          {
                            id: 'name',
                            label: 'USE-CASES',
                            sortable: false,
                          },
                          {
                            id: 'count',
                            label: 'No. OF CAMPAIGNS',
                            sortable: false,
                          },
                        ]}
                        tableData={useCaseTable.tableData.map((d) => (
                          <UseCaseTableRow data={d} key={d.name} />
                        ))}
                        loading={useCaseTable.loading}
                        emptyState="no use-cases to view"
                      />
                    </div>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>
      <GeneralAlertPopup />
    </section>
  );
};

export default withAuthContext(DashboardNew);
